import { useCallback } from 'react';
import { JsonValue } from 'type-fest';

import { refreshPartThumbnailImage } from '@frontend/api';

import { useSubscribeWebsocket } from './useSubscribeWebsocket';

/**
 * Refetch a part file's thumbnail when the server announces it replaced it.
 *
 * The websocket service camel-cases message keys, so the server's `part_file_uuid`
 * arrives here as `partFileUuid`.
 *
 * The server publishes to the whole account, like task status, so this is mounted
 * once for the app rather than per page. Only the thumbnail named in the message
 * is touched, and only if something has fetched it.
 */
export const useThumbnailUpdatedRefresh = () => {
  const onThumbnailUpdated = useCallback((message: JsonValue) => {
    const partFileUuid =
      message !== null && typeof message === 'object' && !Array.isArray(message)
        ? (message as Record<string, JsonValue>).partFileUuid
        : null;
    if (typeof partFileUuid === 'string') {
      refreshPartThumbnailImage(partFileUuid);
    }
  }, []);

  useSubscribeWebsocket('parts.thumbnail_updated', onThumbnailUpdated, 500);
};

/** Mounts :func:`useThumbnailUpdatedRefresh` where a hook cannot go. */
export const ThumbnailUpdatedRefresh = () => {
  useThumbnailUpdatedRefresh();
  return null;
};
